<template lang="pug">
#terminate(v-cloak)
  .box(v-if="validPeriodTo == 0")
    .box-header.with-border.register-header
      .title
        span 解約のお手続き
    .box-body.register-body
      .register-row
        .register-group
          .register-value
            .message
              p 本サービスの解約依頼を行います。
              p 解約を行っても、今月末までは引き続き本サービスをご利用可能です。
    .box-footer.with-border.register-footer
      .buttons
        button.btn.terminate-btn(type="button" @click="onTerminate") 解約を依頼

  .box(v-else)
    .box-header.with-border.register-header
      .title
        span 解約のお手続き
    .box-body.register-body
      .register-row
        .register-group
          .register-value
            .message
              p 現在、解約のご依頼を承っています。
              p 解約のご依頼中でも、今月末までは引き続き本サービスをご利用可能です。
              p.warning-message ※解約後は現在登録している情報は失われます。
    .box-footer.with-border.register-footer
      .buttons
        button.btn.terminate-cancel-btn(type="button" @click="onCancelTerminate") 解約依頼を取消

  TerminateModal(@register="terminate" ref="terminateModal")
  OkModal(v-model="showTerminateSuccessModal" title="解約のご依頼を承りました。\n本サービスをご利用いただきありがとうございました。" @ok="createView")

  OkCancelModal(v-model="showTerminateCancelModal" title="解約依頼を取消します。よろしいですか？" @ok="cancelTerminate")
  OkModal(v-model="showTerminateCancelSuccessModal" title="解約のご依頼を取消しました。\n引き続きよろしくお願いいたします。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import TerminationsService from "@/commons/services/Terminations";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import TerminateModal from "@/pc/components/modals/TerminateModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";

/**
 * 解約画面コンポーネント
 */
@Component({
  components: {
    TerminateModal,
    OkCancelModal,
    OkModal,
    PageLoading
  }
})
export default class Terminate extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 解約サービス */
  @Inject("TerminationsService") terminationsService!: TerminationsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 施設有効期限 */
  validPeriodTo: number = 0;
  /** 解約キャンセル確認モーダル表示可否 */
  showTerminateCancelModal: boolean = false;
  /** 解約依頼完了モーダル表示可否 */
  showTerminateSuccessModal: boolean = false;
  /** 解約依頼キャンセル完了モーダル表示可否 */
  showTerminateCancelSuccessModal: boolean = false;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面を作成する
   *
   * - 施設有効期限を取得する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";
    this.validPeriodTo = await this.getValidPeriodTo();
    this.isBusy = false;
    return;
  }

  /**
   * ### 施設取得
   */
  public async getValidPeriodTo(): Promise<number> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    const validPeriodTo: number = response.validPeriodTo ? response.validPeriodTo : 0;
    return validPeriodTo;
  }

  /**
   * ### 解約押下イベント
   *
   * - 確認ダイアログを表示する
   */
  public async onTerminate(): Promise<void> {
    const modal: any = this.$refs.terminateModal;
    modal.show();
    return;
  }

  /**
   * ### 解約する
   *
   * - APIに解約のリクエストを投げる
   */
  public async terminate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "解約依頼中...";

    await this.terminationsService.post(userStore);
    this.showTerminateSuccessModal = true;
    // ヘッダーメニューの未読バッジ(解約状態)を更新する
    this.$emit("refresh");

    this.isBusy = false;
    return;
  }

  /**
   * ### 解約取消押下イベント
   *
   * - 解約取消確認ダイアログを表示する
   */
  public async onCancelTerminate(): Promise<void> {
    this.showTerminateCancelModal = true;
    return;
  }

  /**
   * ### 解約取消する
   *
   * - APIに解約取消のリクエストを投げる
   */
  public async cancelTerminate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "解約依頼取消中...";

    await this.terminationsService.delete(userStore);
    this.showTerminateCancelSuccessModal = true;
    // ヘッダーメニューの未読バッジ(解約状態)を更新する
    this.$emit("refresh");

    this.isBusy = false;
    return;
  }
}
</script>

<style lang="scss" scoped>
#terminate {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .message {
    p:last-child {
      margin: 0;
    }
  }
}
</style>
>
